/**
 * Get Platform Public Domain URL
 * Returns the domain URL for platform-public based on environment
 *
 * URL Patterns:
 * - Local: use PUBLIC_PORTAL_URL from config, or derive from current port (5004 auth -> 5003 public, 4003 auth -> 4001 public)
 * - Cloud prod: https://b-esper.com
 * - Cloud non-prod: https://{branchcode}.{env}.b-esper.com
 *
 * Priority:
 * 1. PUBLIC_PORTAL_URL from window._env_ / config (central .local or ConfigMap)
 * 2. Localhost: if on platform-auth port, use matching platform-public port (5004->5003, 4003/3002/4002->4001)
 * 3. Cloud hostname / env config
 */

/**
 * Get platform-public domain URL
 * @returns {string} Platform public domain URL
 */
export function getPublicDomain() {
  // Check for explicit PUBLIC_PORTAL_URL environment variable
  const envConfig = window._env_ || {};
  if (envConfig.PUBLIC_PORTAL_URL) {
    return envConfig.PUBLIC_PORTAL_URL.replace(/\/+$/, '');
  }

  // Detect if we're on platform-auth and construct platform-public URL
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const port = window.location.port;
    const protocol = window.location.protocol;

    // Local development: use central config first, then derive from current port
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // If on platform-auth (Node FE): 5004 -> platform-public 5003
      if (port === '5004') {
        return `${protocol}//${hostname}:5003`;
      }
      // If on platform-auth (React/Tilt): 3002, 4002, 4003 -> platform-public 4001
      if (port === '3002' || port === '4002' || port === '4003') {
        return `${protocol}//${hostname}:4001`;
      }
      // If already on platform-public port, return current
      if (port === '4001' || port === '5003') {
        return `${protocol}//${hostname}:${port}`;
      }
      // Default for localhost when port not recognized: 4001 (K8s/Tilt)
      return 'http://localhost:4001';
    }

    // Cloud: Check if we're on platform-auth domain (portal.* subdomain)
    // Platform-auth pattern: portal.{region}.{provider}.b-esper.com (prod) or portal.{branchcode}.{env}.{region}.{provider}.b-esper.com (non-prod)
    // Platform-public pattern: b-esper.com (prod) or {branchcode}.{env}.b-esper.com (non-prod)
    if (hostname.includes('portal.') && hostname.includes('.b-esper.com')) {
      // Extract environment and branch from env config
      const env = envConfig.enviroment || envConfig.environment || '';
      const branch = envConfig.BRANCH_CODE || envConfig.branch_code || '';
      
      // Prod environment: platform-public is at b-esper.com
      // IMPORTANT: Only check env, NOT branch name. branch='main' exists on ALL environments (dev, int, prod).
      if (env === 'prod') {
        return `${protocol}//b-esper.com`;
      }
      
      // Non-prod with branch: platform-public is at {branchcode}.{env}.b-esper.com
      if (env && branch) {
        return `${protocol}//${branch}.${env}.b-esper.com`;
      }
      
      // Non-prod without branch (shouldn't happen, but fallback): {env}.b-esper.com
      if (env) {
        return `${protocol}//${env}.b-esper.com`;
      }
      
      // No env info available — fall through to other detection methods
    }

    // If already on platform-public domain (b-esper.com or subdomain), return current
    if (hostname === 'b-esper.com') {
      return `${protocol}//b-esper.com`;
    }
    
    // Check for {branch}.{env}.b-esper.com pattern (platform-public non-prod)
    if (hostname.endsWith('.b-esper.com')) {
      // If it's not a portal, admin, or api subdomain, it's likely platform-public
      if (!hostname.startsWith('portal.') && !hostname.startsWith('admin.') && !hostname.startsWith('api.')) {
        return `${protocol}//${hostname}`;
      }
    }

    // Docker/K8s: Check for platform-auth port
    if (port === '4002') {
      // Return platform-public port
      return `${protocol}//${hostname}:4001`;
    }

    // Azure Storage static website hosting (legacy/fallback)
    // From site_domain.py: {env}{branch}cust.z1.web.core.windows.net
    if (hostname.includes('.z6.web.core.windows.net') || hostname.includes('.z1.web.core.windows.net')) {
      return `${protocol}//${hostname}`;
    }
  }

  // Fallback: Try to determine from environment config
  const env = envConfig.enviroment || envConfig.environment || process.env.REACT_APP_enviroment || '';
  const branch = envConfig.BRANCH_CODE || envConfig.branch_code || process.env.REACT_APP_BRANCH || '';
  
  // Prod environment: platform-public is at b-esper.com
  // IMPORTANT: Only check env, NOT branch name. branch='main' exists on ALL environments.
  if (env === 'prod') {
    return 'https://b-esper.com';
  }
  
  // Non-prod with branch code: {branchcode}.{env}.b-esper.com
  if (env && branch) {
    return `https://${branch}.${env}.b-esper.com`;
  }
  
  // Non-prod without branch but with env
  if (env) {
    return `https://${env}.b-esper.com`;
  }
  
  // Default to localhost for local development
  return 'http://localhost:4001';
}

/**
 * Check if current page is on platform-auth
 * @returns {boolean} True if on platform-auth
 */
export function isOnPlatformAuth() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  const port = window.location.port;

  // Check for platform-auth indicators
  // Ports: 3002, 4002, 4003 = React/Tilt; 5004 = Node FE auth
  if (port === '3002' || port === '4002' || port === '4003' || port === '5004') {
    return true;
  }

  // Check for portal subdomain
  if (hostname.includes('portal.') && hostname.includes('.b-esper.com')) {
    return true;
  }

  return false;
}

/**
 * Get absolute URL for a public page
 * @param {string} path - Path to the page (e.g., '/about', '/terms')
 * @returns {string} Absolute URL
 */
export function getPublicPageUrl(path) {
  const publicDomain = getPublicDomain();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${publicDomain}${cleanPath}`;
}

/**
 * Get auth portal URL (platform-auth) when on platform-public or for redirects.
 * Use for "Sign in" so public site sends users to the matching auth port on localhost.
 * - localhost:5003 (public) -> http://localhost:5004 (auth)
 * - localhost:4001 (public) -> http://localhost:4003 (auth)
 * @returns {string|null} Auth portal base URL or null if not determinable
 */
export function getAuthPortalUrl() {
  if (typeof window === 'undefined') {
    return null;
  }
  const envConfig = window._env_ || {};
  const authPortalUrl = envConfig.AUTH_PORTAL_URL || envConfig.auth_portal_url || process.env.REACT_APP_AUTH_PORTAL_URL;
  if (authPortalUrl) {
    return authPortalUrl.replace(/\/+$/, '');
  }
  const hostname = window.location.hostname;
  const port = window.location.port;
  const protocol = window.location.protocol;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (port === '5003') return `${protocol}//${hostname}:5004`;
    if (port === '4001') return `${protocol}//${hostname}:4003`;
    return `${protocol}//${hostname}:5004`;
  }
  const customerDomain = envConfig.CUSTOMER_DOMAIN || envConfig.customer_domain;
  if (customerDomain) {
    return `${protocol}//${customerDomain}`.replace(/\/+$/, '');
  }
  if (hostname === 'b-esper.com' || hostname.endsWith('.b-esper.com')) {
    const env = envConfig.enviroment || envConfig.environment || '';
    const branch = envConfig.BRANCH_CODE || envConfig.branch_code || '';
    if (env === 'prod') return 'https://portal.b-esper.com';
    if (env && branch) return `https://portal.${branch}.${env}.b-esper.com`;
    if (env) return `https://portal.${env}.b-esper.com`;
  }
  return null;
}

